import { useCallback, useEffect, useState } from 'react';
import { config } from '@lingoda/config';
import '../styles/googleRecaptchaV3.css';
import { trans } from '@lingoda/i18n';

interface RecaptchaWindow extends Window {
    grecaptcha: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
}

class CaptchaError extends Error {
    public readonly token: string;

    constructor(message: string) {
        super(message);
        this.name = 'CaptchaError';
        this.token = message;
    }
}

declare const window: RecaptchaWindow;

const SCRIPT_ID = 'googleRecaptchaV3';

export const useGoogleRecaptchaV3 = () => {
    const [loading, setLoading] = useState(true);

    const siteKey = config.googleRecaptchaV3SiteKey;

    useEffect(() => {
        if (!siteKey) {
            setLoading(false);

            return;
        }

        const existingScript = document.getElementById(SCRIPT_ID);

        if (existingScript && window.grecaptcha) {
            setLoading(false);

            return;
        }

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
        script.async = true;

        const onload = () => {
            window.grecaptcha.ready(() => {
                setLoading(false);
            });
        };

        script.addEventListener('load', onload);

        document.body.appendChild(script);

        return () => {
            script.removeEventListener('load', onload);
        };
    }, [setLoading, siteKey]);

    const getCaptchaToken = useCallback(async (): Promise<string | undefined> => {
        if (!siteKey) {
            console.debug(
                'useGoogleRecaptchaV3.getCaptchaToken: Google reCAPTCHA v3 site key is not configured',
            );

            return undefined;
        }

        if (loading) {
            return undefined;
        }

        try {
            return await window.grecaptcha.execute(siteKey, { action: 'submit' });
        } catch {
            throw new CaptchaError(trans('captcha.not_passed', {}, 'validators'));
        }
    }, [siteKey, loading]);

    return { getCaptchaToken, loading };
};

export const isCaptchaError = (error: unknown): error is CaptchaError => {
    return error instanceof CaptchaError;
};
